/**
 * useUserCrud - Create/edit/delete operations for user management
 *
 * Extracted from useUserManagement.js. Owns:
 * - The pending-edits buffer (editUsers) and its save* updaters
 * - User creation (API call + optimistic local state)
 * - User editing (password reset, roles, org, org access)
 * - Local removal after deletion
 *
 * The parent hook owns the loaded user map (existingUsers), so its setter is
 * injected along with the form/filter hooks it coordinates with.
 *
 * @param {string} organization - Default organization
 * @param {Object} deps
 * @param {Object} deps.formHook - useUserForm instance
 * @param {Object} deps.filtersHook - useUserFilters instance
 * @param {Function} deps.setExistingUsers - Parent setter for the loaded user map
 * @param {Function} deps.handleError - Parent error handler
 */
import { useState, useCallback } from 'react';

import Util from '../util/util.js';
import { showSuccess } from '../util/error';
import { generateTemporaryPassword } from '../util/user';
import { t } from '../i18n';

export default function useUserCrud(organization, { formHook, filtersHook, setExistingUsers, handleError }) {
  const [editUsers, setEditUsers] = useState({});

  /**
   * Create a new user
   * @param {string} new_user - Username
   * @param {string} temp_password - Temporary password
   * @param {string} new_email - Email address
   * @param {string} new_role - Role to assign
   * @param {string} defaultOrg - Default organization
   * @param {string[]} organization_access - Organizations the user can access
   * @param {Object} subscriptions - Optional subscription preferences for Admin users
   * @param {boolean} subscriptions.detail - Subscribe to report notifications
   * @param {boolean} subscriptions.summary - Subscribe to weekly summary
   */
  const postCreateUser = useCallback(async (
    new_user,
    temp_password,
    new_email,
    new_role,
    defaultOrg,
    organization_access,
    subscriptions = null,
  ) => {
    new_user = new_user.toLowerCase();
    new_email = new_email.toLowerCase();

    await Util.post('UserManagement', '/users', {
      body: {
        username: new_user,
        password: temp_password,
        email: new_email,
        organization: defaultOrg,
        roles: [new_role],
        organization_access,
        ...(subscriptions && { subscriptions }),
      },
    });

    const userData = {
      UserStatus: 'Pending',
      roles: [new_role],
      organization: defaultOrg,
      ...(organization_access?.length && { organization_access }),
    };

    filtersHook.setFilteredUsers((prev) => ({ ...prev, [new_email]: userData }));
    setExistingUsers((prev) => ({ ...prev, [new_email]: userData }));
    showSuccess(t('admin:users.created'));
  }, [filtersHook, setExistingUsers]);

  /**
   * Create user handler
   * Auto-generates password if not provided (always in production, optional override in dev)
   * @param {Object} subscriptions - Optional subscription preferences for Admin users
   */
  const createUser = useCallback(async (subscriptions = null) => {
    formHook.setDisplayErrors(true);
    if (!formHook.isFormValid) return;

    formHook.setUpdatingUser(true);
    try {
      // Auto-generate password if empty
      const password = formHook.password?.trim() || generateTemporaryPassword();

      await postCreateUser(
        formHook.username,
        password,
        formHook.email,
        formHook.rolesFilterValue,
        filtersHook.OrgFilterValue2 ?? organization,
        filtersHook.OrgFilterValue3,
        subscriptions,
      );
      formHook.clearInput();
      filtersHook.setOrgFilterValue([filtersHook.OrgFilterValue2 ?? organization]);
    } catch (err) {
      // Don't override error message - let backend's descriptive message (e.g., domain validation) show
      handleError(err);
    }
    formHook.setUpdatingUser(false);
  }, [formHook, filtersHook, organization, postCreateUser, handleError]);

  /**
   * Edit user handler
   */
  const editUser = useCallback(async (targetUsername) => {
    if (!editUsers.hasOwnProperty(targetUsername)) return;

    const editInfo = editUsers[targetUsername];
    const newPassword = editInfo.password || '';
    const new_roles = editInfo.roles || [];
    const new_org = editInfo.organization || '';
    const new_org_access = editInfo.organization_access || [];

    if (!newPassword.length && !new_roles.length && !new_org.length && !new_org_access.length) {
      return;
    }

    try {
      const currentUser = filtersHook.filteredUsers[targetUsername];
      const previous_org = currentUser?.organization || organization;
      const org = new_org || previous_org;
      // Use previous_org (old default) as fallback, not org (which could be the new default)
      const previous_organization_access = currentUser?.organization_access || [previous_org];
      const organization_access = new_org_access.length ? new_org_access : previous_organization_access;
      const previous_roles = currentUser?.roles || [];

      const params = {
        username: targetUsername,
        email: targetUsername,
        organization: org,
        previous_organization: previous_org,
        ...(newPassword.length && { password: newPassword }),
        ...(new_roles.length && { roles: new_roles, previous_roles }),
        ...(organization_access.length && {
          organization_access,
          previous_organization_access,
        }),
      };

      if (new_org.length > 0 || new_org_access.length > 0 || new_roles.length > 0) {
        if (!params.roles) params.roles = currentUser.roles;
        if (!params.previous_roles) params.previous_roles = previous_roles;
        if (!params.organization_access) params.organization_access = currentUser.organization_access;
      }

      await Util.post('UserManagement', '/users/reset', { body: params });

      // Update local state
      const updates = {};
      if (newPassword.length > 0) {
        updates.UserStatus = 'Pending';
        Util.sendEmailNotification(
          '',
          `Your username is ${targetUsername} and temporary password is ${newPassword}`,
          'passwordReset',
          org,
          targetUsername
        );
      }
      if (new_roles.length > 0) updates.roles = new_roles;
      if (new_org.length > 0) updates.organization = new_org;
      if (new_org_access.length > 0) updates.organization_access = new_org_access;

      // Build the complete updated user object
      const updatedUser = {
        ...currentUser,
        ...updates,
      };

      setExistingUsers((prev) => ({
        ...prev,
        [targetUsername]: updatedUser,
      }));
      // Also update filteredUsers directly to ensure UI reflects changes immediately
      filtersHook.setFilteredUsers((prev) => ({
        ...prev,
        [targetUsername]: updatedUser,
      }));
      // Clear editUsers for this user so next session starts fresh
      setEditUsers((prev) => {
        const updated = { ...prev };
        delete updated[targetUsername];
        return updated;
      });
      showSuccess(t('admin:users.modified'));
    } catch (err) {
      handleError(err, { userMessage: t('admin:users.editFailed') });
    }
    formHook.setUpdatingUser(false);
  }, [editUsers, filtersHook, organization, formHook, setExistingUsers, handleError]);

  /**
   * Delete user handler
   */
  const deleteUser = useCallback((targetUsername) => {
    setExistingUsers((prev) => {
      const updated = { ...prev };
      delete updated[targetUsername];
      return updated;
    });
    filtersHook.setFilteredUsers((prev) => {
      const updated = { ...prev };
      delete updated[targetUsername];
      return updated;
    });
    filtersHook.setSearchText('');
  }, [filtersHook, setExistingUsers]);

  /**
   * Save edit user updates
   */
  const savePasswordReset = useCallback((targetUsername, newPassword) => {
    setEditUsers((prev) => ({
      ...prev,
      [targetUsername]: { ...prev[targetUsername], password: newPassword },
    }));
  }, []);

  const saveRoleUpdate = useCallback((targetUsername, roles) => {
    setEditUsers((prev) => ({
      ...prev,
      [targetUsername]: { ...prev[targetUsername], roles },
    }));
  }, []);

  const saveOrgUpdate = useCallback((targetUsername, org) => {
    setEditUsers((prev) => {
      const currentEdit = prev[targetUsername] || {};
      // Get current org_access from editUsers or fall back to filteredUsers
      const currentOrgAccess = currentEdit.organization_access
        || filtersHook.filteredUsers[targetUsername]?.organization_access
        || [];

      // Ensure new default org is in organization_access
      const updatedOrgAccess = currentOrgAccess.includes(org)
        ? currentOrgAccess
        : [...currentOrgAccess, org];

      return {
        ...prev,
        [targetUsername]: {
          ...currentEdit,
          organization: org,
          organization_access: updatedOrgAccess,
        },
      };
    });
  }, [filtersHook.filteredUsers]);

  const saveOrgAccessUpdate = useCallback((targetUsername, organizations) => {
    setEditUsers((prev) => ({
      ...prev,
      [targetUsername]: { ...prev[targetUsername], organization_access: organizations },
    }));
  }, []);

  return {
    editUsers,
    postCreateUser,
    createUser,
    editUser,
    deleteUser,
    savePasswordReset,
    saveRoleUpdate,
    saveOrgUpdate,
    saveOrgAccessUpdate,
  };
}
